import emailjs from '@emailjs/browser';

// EmailJS Configuration
// Sign up at https://www.emailjs.com/ to get your credentials
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';

// Template IDs
const TEMPLATE_APPROVED = process.env.REACT_APP_EMAILJS_TEMPLATE_APPROVED || 'template_approved';
const TEMPLATE_DECLINED = process.env.REACT_APP_EMAILJS_TEMPLATE_DECLINED || 'template_declined';

/**
 * Initialize EmailJS
 */
export const initializeEmailJS = () => {
  console.log('Initializing EmailJS with:', {
    serviceId: EMAILJS_SERVICE_ID,
    publicKey: EMAILJS_PUBLIC_KEY ? EMAILJS_PUBLIC_KEY.substring(0, 5) + '...' : 'NOT SET',
    templateApproved: TEMPLATE_APPROVED,
    templateDeclined: TEMPLATE_DECLINED
  });
  emailjs.init(EMAILJS_PUBLIC_KEY);
};

/**
 * Test email function
 */
export const sendTestEmail = async (testEmail) => {
  try {
    console.log('Sending test email to:', testEmail);
    console.log('Using service ID:', EMAILJS_SERVICE_ID);
    console.log('Using template ID:', TEMPLATE_APPROVED);
    console.log('Using public key:', EMAILJS_PUBLIC_KEY);
    
    // Try multiple variable name formats to match EmailJS templates
    const templateParams = {
      to_email: testEmail,
      email: testEmail,
      user_email: testEmail,
      to_name: 'Test User',
      name: 'Test User',
      membership_tier: 'Silver',
      login_url: window.location.origin + '/login',
      app_url: window.location.origin
    };
    
    console.log('Template params:', templateParams);

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      TEMPLATE_APPROVED,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    console.log('Test email sent successfully:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send test email:', error);
    console.error('Error details:', {
      message: error.message,
      text: error.text,
      status: error.status,
      fullError: error
    });
    
    // Return more detailed error message
    let errorMessage = error.text || error.message || 'Unknown error';
    
    if (error.status === 400) {
      errorMessage = 'Bad Request (400): Check if your Service ID and Template ID are correct in EmailJS dashboard';
    } else if (error.status === 403) {
      errorMessage = 'Forbidden (403): Check if your Public Key is correct';
    }
    
    return { success: false, error: errorMessage };
  }
};

/**
 * Send approval email
 * @param {Object} application - The application data
 */
export const sendApprovalEmail = async (application) => {
  try {
    console.log('Sending approval email to:', application.email);
    
    const templateParams = {
      to_email: application.email,
      email: application.email,
      user_email: application.email,
      to_name: `${application.first_name} ${application.last_name}`,
      name: `${application.first_name} ${application.last_name}`,
      membership_tier: application.membership_tier,
      login_url: window.location.origin + '/login',
      app_url: window.location.origin
    };

    console.log('Approval email params:', templateParams);

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      TEMPLATE_APPROVED,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    console.log('Approval email sent successfully:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send approval email:', error);
    console.error('Error details:', {
      message: error.message,
      text: error.text,
      status: error.status
    });
    return { success: false, error: error.text || error.message };
  }
};

/**
 * Send decline email
 * @param {Object} application - The application data
 */
export const sendDeclineEmail = async (application) => {
  try {
    console.log('Sending decline email to:', application.email);
    
    const templateParams = {
      to_email: application.email,
      email: application.email,
      user_email: application.email,
      to_name: `${application.first_name} ${application.last_name}`,
      name: `${application.first_name} ${application.last_name}`,
      membership_tier: application.membership_tier,
      support_email: 'support@wealthclub.com'
    };

    console.log('Decline email params:', templateParams);

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      TEMPLATE_DECLINED,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    console.log('Decline email sent successfully:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send decline email:', error);
    console.error('Error details:', {
      message: error.message,
      text: error.text,
      status: error.status
    });
    return { success: false, error: error.text || error.message };
  }
};

/**
 * Send notification email based on status
 * @param {Object} application - The application data
 * @param {string} status - 'approved' or 'declined'
 */
export const sendStatusNotification = async (application, status) => {
  if (status === 'approved') {
    return await sendApprovalEmail(application);
  } else if (status === 'declined') {
    return await sendDeclineEmail(application);
  } else {
    console.warn('No email sent - status is not approved or declined');
    return { success: false, error: 'Invalid status' };
  }
};
